import React from "react";
import { APP_NAME } from "../lib/brand.js";

/**
 * Landing: the page body of the product landing (see EntryFork.jsx for why
 * it reads the way it does).
 *
 * Imports nothing from react-router on purpose. EntryFork passes the router's
 * <Link> in; scripts/build-coming-soon.mjs renders <Landing comingSoon /> in
 * plain Node, where react-router-dom's CommonJS build can't be loaded as named
 * exports on every install (Oct 18). With comingSoon, no Link is ever rendered.
 */

function Mark() {
  // Same drawing as components/Wordmark.jsx (which imports the router).
  return (
    <span style={s.mark}>
      <span aria-hidden="true" style={s.markDot} />{APP_NAME}
    </span>
  );
}

const AGENCY_MAIL = "mailto:info@vizette.net?subject=Tell%20the%20Team%20for%20our%20agency";

const GETS = [
  "Who she is, in the words of people who love her",
  "What her days look like, and what matters in them",
  "The people in her life",
  "How to be with her well",
];

const STEPS = [
  { title: "People who know her tell her story",
    text:  "Family, friends and longtime staff answer a short guided interview, by voice or by typing, and add the photos and documents they already have." },
  { title: "A person shapes it",
    text:  "AI drafts her profile from what they said. A coordinator or family organizer reviews every part, and nothing reaches her team until they approve it." },
  { title: "Her team meets her",
    text:  "Each support professional gets a narrated portrait, a few minutes long, on their phone — then a minute at a time, as the days go on." },
];

export function Landing({ comingSoon = false, Link = null }) {
  const Arrow = () => <span aria-hidden="true" style={{ fontSize: 17, lineHeight: 1 }}>→</span>;
  return (
    <div className="tt-page" style={s.page}>
      <style>{CSS}</style>
      {comingSoon && <div className="tt-watermark" aria-hidden="true"><span>Coming soon</span></div>}
      <div style={s.inner}>

        <header className="tt-topbar" style={s.topbar}>
          <Mark />
          {comingSoon
            ? <span style={s.soonBadge}>Coming soon</span>
            : <Link to="/login" className="tt-btn-quiet" style={s.loginLink}>Log in</Link>}
        </header>

        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <section className="tt-hero" style={s.hero}>
          <div>
            <p style={s.eyebrow}>Person-centered orientation for Direct Support Professionals</p>
            {/* Broken across two lines on purpose: the sentence turns there. */}
            <h1 className="tt-h1" style={s.h1}>Before they care for her,<br />let them meet her.</h1>
            <p style={s.lede}>
              You know who she really is. Now, her support person can get to know
              her, too.
            </p>

            <div style={s.doors}>
              <div style={s.door}>
                <p style={s.doorWho}>For families</p>
                <p style={s.doorWhat}>
                  Set up a profile for someone you care about. Free to start.
                  A first draft in a few minutes.
                </p>
                {!comingSoon && (
                  <Link to="/start/family" className="tt-btn-primary" style={s.primary}>
                    Start a profile <Arrow />
                  </Link>
                )}
              </div>
              <div style={s.door}>
                <p style={s.doorWho}>For support agencies</p>
                {comingSoon ? (
                  <p style={s.doorWhat}>We'll set up your agency portal, personally.</p>
                ) : (
                  <p style={s.doorWhat}>
                    We'll set up your agency portal, personally. Reach out, and
                    we'll reach back. Or{" "}
                    <Link to="/start/family" style={s.doorLink}>try a family profile</Link>
                    {" "}to check it out.
                  </p>
                )}
                {!comingSoon && (
                  <a href={AGENCY_MAIL} className="tt-btn-primary" style={s.primary}>
                    Set up your agency <Arrow />
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* What the product makes, described rather than mocked up: no
              photo, no players, nothing that looks like it should respond. */}
          <aside className="tt-card" style={s.card}>
            <p style={s.cardKicker}>What her team gets</p>
            <p style={s.cardLead}>A narrated portrait of her, a few minutes long.</p>
            <ul style={s.getList}>
              {GETS.map(g => (
                <li key={g} style={s.getItem}>
                  <span aria-hidden="true" style={s.getDot} />{g}
                </li>
              ))}
            </ul>
            <p style={s.cardFoot}>
              Not a care plan. The small true things that never fit in one.
            </p>
          </aside>
        </section>

        <section className="tt-band" style={s.band}>
          <p style={s.bandText}>
            You — and the people who know and love her best — can easily tell
            her story. Talk about her. Share your memories. Add photos that help
            bring her to life.
          </p>
          <p style={s.bandText}>
            Together, you can share the sweet, the remarkable, the difficult,
            and all the little things that make her <em>her</em> — so caregiving
            begins with understanding the person.
          </p>
        </section>

        {/* ── How it works ─────────────────────────────────────────────── */}
        <section style={s.section}>
          <h2 style={s.h2}>How it works</h2>
          <ol className="tt-steps" style={s.steps}>
            {STEPS.map((st, i) => (
              <li key={st.title} style={s.step}>
                <span style={s.stepNum}>{i + 1}</span>
                <p style={s.stepTitle}>{st.title}</p>
                <p style={s.stepText}>{st.text}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* ── Who makes it (from vizette.net) ──────────────────────────── */}
        <section style={s.section}>
          <h2 style={s.h2}>Shaped by the people who do the work</h2>
          <div className="tt-about" style={s.about}>
            <p style={s.bandText}>
              We use AI guided by people to create narrated, moving portraits
              for the support sector — practical tools that improve quality of
              care while reducing manager overhead.
            </p>
            <p style={s.bandText}>
              Working with experienced practitioners on the frontlines, we've
              shaped Tell the Team to the real needs of day-to-day support.
            </p>
          </div>
        </section>

        <footer style={s.footer}>
          Tell the Team is made by{" "}
          {comingSoon ? "Vizette" : <a href="https://vizette.net" style={s.footLink}>Vizette</a>}.
        </footer>
      </div>
    </div>
  );
}

const C = {
  bg: "#f7f5f2", surface: "#ffffff", border: "#dedad4", borderMid: "#c8c4bc",
  ink: "#1a1915", ink2: "#5a5850", ink3: "#8f8c83",
  accent: "#2d6a4f", accentBg: "#d8ece3", accentText: "#3f7a60",
};
const SERIF = "Georgia, 'Lora', serif";

/* Responsive rules and the watermark live in CSS so the static render behaves
   the same as the app. 640px mirrors useIsCompact. */
const CSS = `
.tt-btn-primary:hover { background: #245a42 }
.tt-btn-quiet:hover { border-color: #8f8c83; color: #1a1915 }
.tt-btn-primary:focus-visible, .tt-btn-quiet:focus-visible { outline: 2px solid #2d6a4f; outline-offset: 3px }
.tt-watermark {
  position: fixed; inset: 0; display: flex; align-items: center; justify-content: center;
  pointer-events: none; z-index: 5; overflow: hidden; user-select: none;
}
/* The span rotates, not the box: rotating the clipped box would cut the words off. */
.tt-watermark span {
  font-family: ${SERIF}; font-size: clamp(72px, 13vw, 200px); white-space: nowrap;
  color: rgba(45,106,79,0.13); transform: rotate(-24deg);
}
@media (max-width: 860px) {
  .tt-hero { grid-template-columns: minmax(0,1fr) !important; gap: 32px !important }
  .tt-card { max-width: 440px; width: 100%; box-sizing: border-box; justify-self: center }
}
@media (max-width: 640px) {
  .tt-page { padding: 20px 16px 56px !important }
  .tt-topbar { margin-bottom: 28px !important }
  .tt-h1 { font-size: 30px !important; line-height: 1.2 !important }
  .tt-band { margin-top: 40px !important; padding-top: 28px !important }
  .tt-watermark span { font-size: 22vw; transform: rotate(-62deg) }
}
`;

const s = {
  page:      { minHeight: "100dvh", background: C.bg, padding: "40px 24px 72px", fontFamily: "'Inter', system-ui, sans-serif", overflowX: "hidden", color: C.ink },
  inner:     { maxWidth: 1060, margin: "0 auto", position: "relative" },
  topbar:    { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 56, gap: 16 },
  loginLink: { color: C.ink2, textDecoration: "none", fontSize: 14, padding: "7px 16px", border: `1px solid ${C.borderMid}`, borderRadius: 8, transition: "border-color 150ms ease, color 150ms ease" },
  soonBadge: { fontSize: 12, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: C.accent, background: C.accentBg, padding: "6px 12px", borderRadius: 999 },

  hero:      { display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,380px)", gap: 56, alignItems: "center" },
  eyebrow:   { fontSize: 13, fontWeight: 600, letterSpacing: "0.04em", color: C.accentText, margin: "0 0 14px" },
  h1:        { fontFamily: SERIF, fontSize: 40, lineHeight: 1.22, color: C.ink, margin: "0 0 14px", letterSpacing: "-0.4px", fontWeight: 400 },
  lede:      { fontSize: 17, color: C.ink2, lineHeight: 1.65, margin: "0 0 34px", maxWidth: 470 },
  doors:     { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(230px, 100%), 1fr))", gap: 14, maxWidth: 560 },
  door:      { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: "18px 20px", display: "flex", flexDirection: "column", gap: 10 },
  doorWho:   { fontSize: 12, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: C.ink3, margin: 0 },
  doorWhat:  { fontSize: 14.5, lineHeight: 1.55, color: C.ink2, margin: 0, flexGrow: 1 },
  doorLink:  { color: C.accentText, textDecoration: "underline", textUnderlineOffset: 3 },
  primary:   { background: C.accent, color: "#fff", textDecoration: "none", fontSize: 15, fontWeight: 500, padding: "14px 28px", borderRadius: 8, display: "inline-flex", alignItems: "center", gap: 8, transition: "background 150ms ease", alignSelf: "flex-start" },

  card:      { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16, padding: 28, boxShadow: "0 1px 3px rgba(26,25,21,0.05), 0 12px 32px rgba(26,25,21,0.07)" },
  cardKicker:{ fontSize: 12, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: C.ink3, margin: "0 0 10px" },
  cardLead:  { fontFamily: SERIF, fontSize: 21, lineHeight: 1.4, color: C.ink, margin: "0 0 18px" },
  getList:   { listStyle: "none", padding: 0, margin: "0 0 20px", display: "flex", flexDirection: "column", gap: 11 },
  getItem:   { display: "flex", alignItems: "baseline", gap: 10, fontSize: 15, lineHeight: 1.5, color: C.ink2 },
  getDot:    { width: 7, height: 7, borderRadius: "50%", background: C.accent, flexShrink: 0, transform: "translateY(-1px)" },
  cardFoot:  { fontFamily: SERIF, fontSize: 15.5, lineHeight: 1.5, color: C.ink, margin: 0, borderTop: `1px solid ${C.border}`, paddingTop: 16 },

  band:      { marginTop: 56, paddingTop: 40, borderTop: `1px solid ${C.border}`, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(300px, 100%), 1fr))", gap: 32, maxWidth: 920 },
  bandText:  { fontSize: 17, lineHeight: 1.7, color: C.ink2, margin: 0 },

  section:   { marginTop: 48, paddingTop: 40, borderTop: `1px solid ${C.border}` },
  h2:        { fontFamily: SERIF, fontSize: 26, lineHeight: 1.35, color: C.ink, margin: "0 0 22px", letterSpacing: "-0.3px", fontWeight: 400 },
  steps:     { listStyle: "none", padding: 0, margin: 0, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(250px, 100%), 1fr))", gap: 14 },
  step:      { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: "20px 22px" },
  stepNum:   { display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: "50%", background: C.accentBg, color: C.accent, fontSize: 14, fontWeight: 600, marginBottom: 12 },
  stepTitle: { fontSize: 16, fontWeight: 500, color: C.ink, margin: "0 0 6px" },
  stepText:  { fontSize: 14.5, lineHeight: 1.6, color: C.ink2, margin: 0 },
  about:     { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(300px, 100%), 1fr))", gap: 32, maxWidth: 920 },

  footer:    { marginTop: 56, paddingTop: 24, borderTop: `1px solid ${C.border}`, fontSize: 13.5, color: C.ink3 },
  footLink:  { color: C.ink2, textUnderlineOffset: 3 },
  mark:      { fontWeight: "var(--weight-medium)", fontSize: 17, letterSpacing: "-0.3px", color: "var(--color-text-primary)", display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" },
  markDot:   { display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "var(--color-accent)" },
};
